import { type AccessTokenClaims } from '@aysar/contracts';
import { useMemo } from 'react';

import { selectAccessToken } from '@/shared/store/reducers/auth/auth.slice';
import { useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';
import { decodeAccessClaims } from '@/shared/utils/access-claims/access-claims.util';

export interface Session {
  isAuthenticated: boolean;
  claims: AccessTokenClaims | null;
  /** The actor's own user id, for a self-scoped read. */
  userId: string | null;
  isSuperAdmin: boolean;
}

/**
 * Who is signed in, according to the access token this client is holding.
 *
 * The token is **decoded, not verified** — there is no key here and there never
 * will be. Everything this returns is for RENDERING: which nav items to draw,
 * whether to show the shell at all. The api is the only authority, and it
 * re-checks `tokenVersion` on every single request (N8/N22), so a token this
 * client still thinks is good dies on its next use.
 *
 * A malformed or expired-looking token decodes to `null`, which reads as signed
 * out — fail closed.
 */
export function useSession(): Session {
  const accessToken = useAppSelector(selectAccessToken);

  return useMemo(() => {
    const claims = accessToken === null ? null : decodeAccessClaims(accessToken);

    return {
      isAuthenticated: claims !== null,
      claims,
      userId: claims?.sub ?? null,
      isSuperAdmin: claims?.sa ?? false,
    };
  }, [accessToken]);
}
